import { Box, Divider, Flex, Text, Badge } from '@chakra-ui/react';
import { WizardState } from '../types';
import { format } from 'date-fns';

interface StepSummaryProps {
  state: WizardState;
}

const StepSummary = ({ state }: StepSummaryProps) => {
  const { type, sendTokensData, contractData, tokenAssociationData, options } = state;

  const formatExpiration = () => {
    if (options.expirationTime) {
      return format(options.expirationTime, 'PPpp'); // e.g., "Apr 29, 2024, 4:30 PM"
    }
    return 'Maximum (62 days)';
  };

  return (
    <Flex direction="column" gap={4} w="full">
      <Box p={4} bg="green.50" borderRadius={12} >
        <Text size="sm" color="green.700" fontWeight={500}>
          Please review your transaction details before submitting.
        </Text>
      </Box>

      {/* Token Association Notice */}
      {type === 'TOKEN_ASSOCIATE' && (
        <Box p={4} bg="blue.50" borderRadius={12} border="1px solid" borderColor="blue.200">
          <Text size="sm" color="blue.700" fontWeight={500} mb={1}>
            Multi-Signature Token Association
          </Text>
          <Text size="xs" color="blue.600">
            Token association requires manual signature collection. Other signers will need
            to sign before the expiration time for the transaction to execute.
          </Text>
        </Box>
      )}

      {/* Transaction Type */}
      <Box>
        <Text size="xs" color="gray.500" mb={1}>Transaction Type</Text>
        <Text fontWeight={600}>
          {type === 'SEND_TOKENS' ? 'Send Tokens' : type === 'TOKEN_ASSOCIATE' ? 'Associate Token' : 'Contract Interaction'}
        </Text>
      </Box>

      <Divider />

      {/* Send Tokens Summary */}
      {type === 'SEND_TOKENS' && (
        <>
          <Flex gap={4}>
            <Box flex={1}>
              <Text size="xs" color="gray.500" mb={1}>Recipient</Text>
              <Text fontWeight={500} wordBreak="break-all">
                {sendTokensData.address}
              </Text>
            </Box>
            <Box flex={1}>
              <Text size="xs" color="gray.500" mb={1}>Amount</Text>
              <Text fontWeight={600} color="green.600">
                {sendTokensData.amount} {sendTokensData.tokenSymbol}
              </Text>
            </Box>
          </Flex>
          {sendTokensData.description && (
            <Box>
              <Text size="xs" color="gray.500" mb={1}>Description</Text>
              <Text>{sendTokensData.description}</Text>
            </Box>
          )}
        </>
      )}

      {/* Contract Interaction Summary */}
      {type === 'CONTRACT_INTERACTION' && (
        <>
          <Box>
            <Text size="xs" color="gray.500" mb={1}>Contract ID</Text>
            <Text fontWeight={500}>{contractData.contractId}</Text>
          </Box>
          <Box>
            <Text size="xs" color="gray.500" mb={1}>Method</Text>
            <Text fontWeight={600} fontFamily="mono">
              {contractData.selectedMethod}()
            </Text>
          </Box>
          {contractData.methodInputs.length > 0 && (
            <Box>
              <Text size="xs" color="gray.500" mb={2}>Parameters</Text>
              <Box p={3} bg="gray.50" borderRadius={8}>
                {contractData.methodInputs.map((input, idx) => (
                  <Flex key={idx} justify="space-between" py={1}>
                    <Text size="sm" color="gray.600">
                      {input.name} ({input.type})
                    </Text>
                    <Text size="sm" fontWeight={500} maxW="200px" isTruncated>
                      {String(input.value)}
                    </Text>
                  </Flex>
                ))}
              </Box>
            </Box>
          )}
          <Flex gap={4}>
            <Box flex={1}>
              <Text size="xs" color="gray.500" mb={1}>Gas Limit</Text>
              <Text fontWeight={500}>{Number(contractData.gas).toLocaleString()}</Text>
            </Box>
            {Number(contractData.payableAmount) > 0 && (
              <Box flex={1}>
                <Text size="xs" color="gray.500" mb={1}>Payable Amount</Text>
                <Text fontWeight={500}>{contractData.payableAmount} HBAR</Text>
              </Box>
            )}
          </Flex>
        </>
      )}

      {/* Token Association Summary */}
      {type === 'TOKEN_ASSOCIATE' && (
        <>
          <Box>
            <Text size="xs" color="gray.500" mb={1}>Token ID</Text>
            <Text fontWeight={500}>{tokenAssociationData.tokenId}</Text>
          </Box>
          <Flex gap={4}>
            <Box flex={1}>
              <Text size="xs" color="gray.500" mb={1}>Token Name</Text>
              <Text fontWeight={500}>{tokenAssociationData.tokenName}</Text>
            </Box>
            <Box flex={1}>
              <Text size="xs" color="gray.500" mb={1}>Symbol</Text>
              <Text fontWeight={600}>{tokenAssociationData.tokenSymbol}</Text>
            </Box>
          </Flex>
          {tokenAssociationData.description && (
            <Box>
              <Text size="xs" color="gray.500" mb={1}>Description</Text>
              <Text>{tokenAssociationData.description}</Text>
            </Box>
          )}
        </>
      )}

      <Divider />

      {/* Execution Settings */}
      <Box>
        <Text size="sm" fontWeight={600} mb={3}>Execution Settings</Text>
        <Flex gap={4}>
          <Box flex={1}>
            <Text size="xs" color="gray.500" mb={1}>Expires At</Text>
            <Text fontWeight={500}>{formatExpiration()}</Text>
          </Box>
          <Box flex={1}>
            <Text size="xs" color="gray.500" mb={1}>Wait For Expiry</Text>
            <Badge
              colorScheme={options.waitForExpiry ? 'orange' : 'gray'}
              borderRadius={4}
              px={2}
            >
              {options.waitForExpiry ? 'Yes - Time Locked' : 'No'}
            </Badge>
          </Box>
        </Flex>
      </Box>

      {options.waitForExpiry && (
        <Box p={3} bg="orange.50" borderRadius={8}>
          <Text size="xs" color="orange.700">
            This transaction will only execute at the scheduled expiration time,
            even if all required signatures are collected earlier.
          </Text>
        </Box>
      )}
    </Flex>
  );
};

export default StepSummary;
